/**
 * PropertyServices Component
 * Displays service providers for property listings: Valuations, Mortgages, Legal, Insurance
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from './ui/accordion';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { useToast } from '../hooks/use-toast';
import { 
  Home,
  Building2,
  Scale,
  Shield,
  Star,
  MapPin,
  Phone,
  Globe,
  Clock,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Info,
  Banknote,
  FileText,
  Heart,
  ExternalLink
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons and colors
const CATEGORY_CONFIG = {
  valuation: {
    icon: Home,
    color: 'from-blue-500 to-cyan-600',
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-700',
    title: 'Property Valuation',
    description: 'Professional valuers and surveyors near this property'
  },
  mortgage: {
    icon: Building2,
    color: 'from-green-500 to-emerald-600',
    bgColor: 'bg-green-50',
    textColor: 'text-green-700',
    title: 'Mortgage Providers',
    description: 'Banks and mortgage brokers to help finance your purchase'
  },
  legal: {
    icon: Scale,
    color: 'from-purple-500 to-indigo-600',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-700',
    title: 'Legal Services',
    description: 'Conveyancers, solicitors and notaries for your property transaction'
  },
  insurance: {
    icon: Shield,
    color: 'from-orange-500 to-red-600',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-700',
    title: 'Insurance',
    description: 'Home and life insurance providers to protect your investment'
  }
};

// Provider type badges
const PROVIDER_TYPE_LABELS = {
  bank: { label: 'Bank', color: 'bg-blue-100 text-blue-700' },
  building_society: { label: 'Building Society', color: 'bg-green-100 text-green-700' },
  broker: { label: 'Broker', color: 'bg-purple-100 text-purple-700' },
  rics_valuer: { label: 'RICS Valuer', color: 'bg-cyan-100 text-cyan-700' },
  surveyor: { label: 'Surveyor', color: 'bg-teal-100 text-teal-700' },
  estate_agent: { label: 'Estate Agent', color: 'bg-amber-100 text-amber-700' },
  conveyancer: { label: 'Conveyancer', color: 'bg-indigo-100 text-indigo-700' },
  solicitor: { label: 'Solicitor', color: 'bg-violet-100 text-violet-700' },
  notary: { label: 'Notary', color: 'bg-pink-100 text-pink-700' },
  insurer: { label: 'Insurer', color: 'bg-rose-100 text-rose-700' },
  valuer: { label: 'Valuer', color: 'bg-sky-100 text-sky-700' },
  attorney: { label: 'Attorney', color: 'bg-fuchsia-100 text-fuchsia-700' }
};

const PropertyServiceSection = ({ category, listing, country = 'UK' }) => {
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [showEnquiryDialog, setShowEnquiryDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const { toast } = useToast();
  
  const config = CATEGORY_CONFIG[category];
  const IconComponent = config?.icon || Home;

  useEffect(() => {
    fetchProviders();
  }, [category, listing, country]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const params = {
        category,
        country,
        limit: 5
      };
      
      // Add location if available
      if (listing?.latitude && listing?.longitude) {
        params.latitude = listing.latitude;
        params.longitude = listing.longitude;
        params.max_distance = 100; // 100km radius
      }
      
      const response = await axios.get(`${API_URL}/api/property-services/providers`, { params });
      
      if (response.data.success) {
        setProviders(response.data.providers);
      }
    } catch (error) {
      console.error(`Error fetching ${category} providers:`, error);
    } finally {
      setLoading(false);
    }
  };

  const openEnquiry = (provider) => {
    setSelectedProvider(provider);
    setShowEnquiryDialog(true);
  };

  const handleSubmitEnquiry = async () => {
    if (!enquiryForm.name || !enquiryForm.email || !enquiryForm.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      setSubmitting(true);
      
      const response = await axios.post(`${API_URL}/api/property-services/enquiry`, null, {
        params: {
          provider_id: selectedProvider.id,
          listing_id: listing?.id,
          name: enquiryForm.name,
          email: enquiryForm.email,
          phone: enquiryForm.phone,
          message: enquiryForm.message,
          service_type: category
        }
      });

      if (response.data.success) {
        toast({
          title: "Enquiry Sent!",
          description: `Your enquiry has been sent to ${selectedProvider.name}. They will contact you shortly.`,
        });
        setShowEnquiryDialog(false);
        setEnquiryForm({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send enquiry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price, category) => {
    if (price === null || price === undefined) return null;
    if (price === 0) return 'Free';
    
    const currency = country === 'LK' ? 'Rs.' : '£';
    
    if (category === 'mortgage') {
      return `From ${price}% APR`;
    }
    return `From ${currency}${price.toLocaleString()}`;
  };

  const renderProviderCard = (provider) => {
    const typeConfig = PROVIDER_TYPE_LABELS[provider.provider_type] || { label: provider.provider_type, color: 'bg-gray-100 text-gray-700' };
    
    return (
      <Card 
        key={provider.id} 
        className={`relative overflow-hidden transition-all hover:shadow-md ${provider.is_featured ? 'border-yellow-300 bg-yellow-50/30' : ''}`}
        data-testid={`property-service-provider-${provider.id}`}
      >
        {provider.is_featured && (
          <div className="absolute top-0 right-0 bg-yellow-400 text-yellow-900 text-xs font-medium px-2 py-0.5 rounded-bl">
            Featured
          </div>
        )}
        
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${config.bgColor}`}>
              <IconComponent className={`h-6 w-6 ${config.textColor}`} />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-semibold text-gray-900">{provider.name}</h4>
                <Badge variant="outline" className={`text-xs ${typeConfig.color}`}>
                  {typeConfig.label}
                </Badge>
              </div>
              
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">{provider.description}</p>
              
              {/* Rating and Reviews */}
              <div className="flex items-center gap-3 mt-2">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm font-medium">{provider.rating}</span>
                  <span className="text-xs text-gray-500">({provider.review_count} reviews)</span>
                </div>
                {provider.distance_km && (
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <MapPin className="h-3 w-3" />
                    <span>{provider.distance_km} km away</span>
                  </div>
                )}
              </div>
              
              {/* Services */}
              <div className="flex flex-wrap gap-1 mt-2">
                {provider.services?.slice(0, 3).map((service, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs bg-gray-100">
                    {service}
                  </Badge>
                ))}
                {provider.services?.length > 3 && (
                  <Badge variant="secondary" className="text-xs bg-gray-100">
                    +{provider.services.length - 3} more
                  </Badge>
                )}
              </div>
              
              {/* Key Info Grid */}
              <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                {(provider.price_from !== undefined || provider.rate_from) && (
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900">
                      {formatPrice(provider.rate_from || provider.price_from, category)}
                    </div>
                    <div className="text-gray-500">{category === 'mortgage' ? 'Rate' : 'Price'}</div>
                  </div>
                )}
                {provider.turnaround && (
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900 flex items-center justify-center gap-1">
                      <Clock className="h-3 w-3" />
                      {provider.turnaround}
                    </div>
                    <div className="text-gray-500">Turnaround</div>
                  </div>
                )}
                {provider.max_ltv && (
                  <div className="text-center p-2 bg-gray-50 rounded">
                    <div className="font-semibold text-gray-900">{provider.max_ltv}%</div>
                    <div className="text-gray-500">Max LTV</div>
                  </div>
                )}
              </div>
              
              {/* Accreditations */}
              {provider.accreditations?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {provider.accreditations.map((acc, idx) => (
                    <span key={idx} className="text-xs text-green-700 flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      {acc}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2 mt-4">
            <Button 
              className={`flex-1 bg-gradient-to-r ${config.color} hover:opacity-90`}
              onClick={() => openEnquiry(provider)}
              data-testid={`enquire-${provider.id}`}
            >
              Get Quote
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            {provider.website && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => window.open(provider.website, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            )}
            {provider.phone && (
              <Button 
                variant="outline" 
                size="icon"
                onClick={() => window.location.href = `tel:${provider.phone}`}
              >
                <Phone className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Card className="mt-4">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading {config?.title?.toLowerCase()}...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (providers.length === 0) {
    return null; // Don't show section if no providers
  }

  return (
    <>
      <Card className="mt-4" data-testid={`property-services-${category}`}>
        <Accordion type="single" collapsible defaultValue={category}>
          <AccordionItem value={category} className="border-0">
            <AccordionTrigger className="px-6 py-4 hover:no-underline">
              <div className="flex items-center gap-3">
                <div className={`p-2 bg-gradient-to-br ${config.color} rounded-lg`}>
                  <IconComponent className="h-5 w-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold">{config.title}</h3>
                  <p className="text-sm text-gray-500 font-normal">
                    {config.description}
                  </p>
                </div>
              </div>
            </AccordionTrigger>
            
            <AccordionContent className="px-6 pb-6">
              <div className="space-y-4">
                {providers.map(provider => renderProviderCard(provider))}
              </div>
              
              <Alert className="mt-4 bg-gray-50 border-gray-200">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs text-gray-600">
                  Service providers are shown based on proximity to this property. 
                  Compare quotes from multiple providers before making a decision.
                </AlertDescription>
              </Alert>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>

      {/* Enquiry Dialog */}
      <Dialog open={showEnquiryDialog} onOpenChange={setShowEnquiryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <IconComponent className={`h-5 w-5 ${config.textColor}`} />
              Contact {selectedProvider?.name}
            </DialogTitle>
            <DialogDescription>
              Fill in your details and we'll connect you with this provider
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Property Info */}
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="font-medium text-blue-900 text-sm">{listing?.title}</div>
              <div className="text-xs text-blue-700">{listing?.location}</div>
            </div>
            
            <div>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="Enter your name"
                value={enquiryForm.name}
                onChange={(e) => setEnquiryForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={enquiryForm.email}
                onChange={(e) => setEnquiryForm(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="phone">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Your phone number"
                value={enquiryForm.phone}
                onChange={(e) => setEnquiryForm(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            
            <div>
              <Label htmlFor="message">Message (Optional)</Label>
              <Textarea
                id="message"
                placeholder="Any specific requirements or questions?"
                value={enquiryForm.message}
                onChange={(e) => setEnquiryForm(prev => ({ ...prev, message: e.target.value }))}
                rows={3}
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setShowEnquiryDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              className={`flex-1 bg-gradient-to-r ${config.color}`}
              onClick={handleSubmitEnquiry}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Send Enquiry
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Main PropertyServices component that renders all service sections
 */
const PropertyServices = ({ listing }) => {
  // Determine country from listing - use coordinates for accurate detection
  // Sri Lanka: latitude ~5.9 to 9.9, longitude ~79.5 to 81.9
  const detectCountry = () => {
    // First check explicit country field
    if (listing?.country) return listing.country;
    
    // Use coordinates if available (most reliable)
    if (listing?.latitude && listing?.longitude) {
      const lat = parseFloat(listing.latitude);
      const lon = parseFloat(listing.longitude);
      // Sri Lanka bounds
      if (lat >= 5.9 && lat <= 9.9 && lon >= 79.5 && lon <= 81.9) {
        return 'LK';
      }
    }
    
    // Fallback to location string check for Sri Lankan cities
    const location = listing?.location?.toLowerCase() || '';
    const slLocations = ['colombo', 'kandy', 'galle', 'nugegoda', 'rajagiriya', 'dehiwala', 
      'moratuwa', 'negombo', 'battaramulla', 'kottawa', 'maharagama', 'piliyandala',
      'sri lanka', 'sri jayawardenepura'];
    if (slLocations.some(loc => location.includes(loc))) {
      return 'LK';
    }
    
    // Default to UK
    return 'UK';
  };
  
  const country = detectCountry();
  
  // Check if this is a property listing
  const isPropertyListing = listing?.category?.toLowerCase().includes('property') || 
    ['property-for-sale', 'property-for-rent', 'rooms-flatshares', 'student-accommodation', 
     'commercial-property', 'land-plots', 'new-developments', 'short-term-holiday-rentals', 
     'overseas-property'].includes(listing?.category);
  
  if (!isPropertyListing) {
    return null;
  }

  return (
    <div className="space-y-4" data-testid="property-services-section">
      <PropertyServiceSection category="valuation" listing={listing} country={country} />
      <PropertyServiceSection category="mortgage" listing={listing} country={country} />
      <PropertyServiceSection category="legal" listing={listing} country={country} />
      <PropertyServiceSection category="insurance" listing={listing} country={country} />
    </div>
  );
};

export default PropertyServices;
